import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Copy01Icon,
  MessageDotsCircleIcon,
  Trash01Icon,
  PlayIcon,
} from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import { AlertDialog } from '@/components/ui/AlertDialog'
import { toast } from '@/lib/toast'
import { useDeleteConversation } from '@/hooks/api/useConversations'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import type { Conversation } from '@/types/conversation'

interface ConversationAdminProps {
  conversation: Conversation
  className?: string
}

export function ConversationAdmin({ conversation, className }: ConversationAdminProps) {
  const { t } = useTranslation()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const deleteConversation = useDeleteConversation()

  const handleCopyUrl = async () => {
    const url = `${window.location.origin}/conversations/${conversation.id}`
    try {
      await navigator.clipboard.writeText(url)
      toast.success(t('resources.conversations.admin.url_copied'))
    } catch {
      toast.error(t('resources.conversations.admin.url_copy_failed'))
    }
  }

  const handleCopyConversationId = async () => {
    if (!conversation.twilio_id) {
      return
    }

    try {
      await navigator.clipboard.writeText(conversation.twilio_id)
      toast.success(t('resources.conversations.admin.conversation_id_copied'))
    } catch {
      toast.error(t('resources.conversations.admin.conversation_id_copy_failed'))
    }
  }

  const handleCopyMessages = async () => {
    const messages =
      typeof conversation.messages === 'string'
        ? conversation.messages
        : JSON.stringify(conversation.messages, null, 2)

    try {
      await navigator.clipboard.writeText(messages)
      toast.success(t('resources.conversations.admin.messages_copied'))
    } catch {
      toast.error(t('resources.conversations.admin.messages_copy_failed'))
    }
  }

  const handleOpenPlayground = () => {
    if (conversation.assistant_id) {
      navigate(
        `/assistants/${conversation.assistant_id}/try/conversation/${conversation.id}`
      )
    }
  }

  const handleDelete = () => {
    deleteConversation.mutate(conversation.id, {
      onSuccess: () => {
        toast.success(t('resources.conversations.admin.deleted'))
        // Preserve filters when navigating back to the list
        const queryString = searchParams.toString()
        navigate(`/conversations${queryString ? `?${queryString}` : ''}`)
      },
      onError: () => {
        toast.error(t('resources.conversations.admin.delete_failed'))
      },
    })
    setShowDeleteConfirm(false)
  }

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        {t('resources.conversations.tabs.admin')}
      </h3>

      <div className="flex flex-col items-start gap-3">
        <Button color="secondary" onPress={handleCopyUrl} className="justify-start" iconLeading={Copy01Icon}>
          {t('resources.conversations.admin.copy_url')}
        </Button>

        {conversation.twilio_id && (
          <Button
            color="secondary"
            onPress={handleCopyConversationId}
            className="justify-start"
            iconLeading={Copy01Icon}
          >
            {t('resources.conversations.admin.copy_conversation_id')}
          </Button>
        )}

        {conversation.messages && (
          <Button
            color="secondary"
            onPress={handleCopyMessages}
            className="justify-start"
            iconLeading={MessageDotsCircleIcon}
          >
            {t('resources.conversations.admin.copy_messages')}
          </Button>
        )}

        {conversation.assistant_id && (
          <Button
            color="secondary"
            onPress={handleOpenPlayground}
            className="justify-start"
            iconLeading={PlayIcon}
          >
            {t('resources.conversations.admin.open_playground')}
          </Button>
        )}

        <Button
          color="secondary"
          onPress={() => setShowDeleteConfirm(true)}
          className="justify-start text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
          iconLeading={Trash01Icon}
        >
          {t('resources.conversations.admin.delete')}
        </Button>
      </div>

      <AlertDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t('resources.conversations.admin.delete_title')}
        description={t('resources.conversations.admin.delete_description')}
        confirmLabel={t('resources.generic.delete')}
        variant="error"
      />
    </div>
  )
}

export default ConversationAdmin
